import * as z from "zod";
import { BOT_TITLE_MAX_LENGTH } from "./bot-settings.js";
import { FACTS_PER_PROPOSAL_MAX, ProposedFactSchema } from "./company-facts.js";
import { AskMessageBlock } from "./events.js";
import { Id } from "./ids.js";
import { ROLE_RECIPE_IDS } from "./role-recipes.js";

/**
 * The company a Space works for, from the setup wizard. Every answer is optional: each step can be
 * skipped, and the owner edits the answers later in Settings. Ids are stable; labels live with the
 * code that shows them.
 */

export const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"] as const;
export type CompanySize = (typeof COMPANY_SIZES)[number];

/** Broad enough to place any business; "other" takes free text. */
export const INDUSTRIES = [
  "security_fm",
  "cleaning",
  "construction",
  "property",
  "professional_services",
  "accounting",
  "legal",
  "consulting",
  "marketing_agency",
  "software",
  "it_services",
  "telecoms",
  "retail",
  "ecommerce",
  "wholesale",
  "manufacturing",
  "engineering",
  "logistics",
  "transport",
  "hospitality",
  "food_beverage",
  "events",
  "travel",
  "healthcare",
  "dental",
  "care",
  "pharma",
  "fitness",
  "beauty",
  "education",
  "childcare",
  "charity",
  "public_sector",
  "financial_services",
  "insurance",
  "recruitment",
  "energy",
  "agriculture",
  "automotive",
  "media",
  "architecture",
  "trades",
  "other",
] as const;
export type Industry = (typeof INDUSTRIES)[number];

/** The departments a new team can cover; each becomes a bot. */
export const DEPARTMENTS = [
  "operations",
  "compliance",
  "finance",
  "bids",
  "hr",
  "sales",
  "customer_service",
  "marketing",
] as const;
export type Department = (typeof DEPARTMENTS)[number];

export const GOALS = [
  "no_uncovered_shifts",
  "paid_faster",
  "win_tenders",
  "no_missed_renewals",
  "fewer_admin_hours",
  "faster_replies",
  "more_leads",
  "hire_faster",
] as const;
export type Goal = (typeof GOALS)[number];

/**
 * How hands-on the bots are, on top of the approval mode. Client emails, payments and deletions
 * ask first whatever is chosen (see autonomyApprovalSettings).
 */
export const AUTONOMY_LEVELS = ["draft", "ask", "act"] as const;
export type Autonomy = (typeof AUTONOMY_LEVELS)[number];

export const TOOL_GROUPS = {
  email: ["microsoft365", "google_workspace"],
  accounting: ["quickbooks", "xero", "sage"],
  files: ["sharepoint", "google_drive", "notion", "hubspot", "whatsapp", "slack", "teams"],
} as const;
export const TOOLS = [
  ...TOOL_GROUPS.email,
  ...TOOL_GROUPS.accounting,
  ...TOOL_GROUPS.files,
] as const;
export type Tool = (typeof TOOLS)[number];

/** Product names, the same in every language. */
export const TOOL_NAMES: Record<Tool, string> = {
  microsoft365: "Microsoft 365 / Outlook",
  google_workspace: "Google Workspace",
  quickbooks: "QuickBooks",
  xero: "Xero",
  sage: "Sage",
  sharepoint: "SharePoint",
  google_drive: "Google Drive",
  notion: "Notion",
  hubspot: "HubSpot",
  whatsapp: "WhatsApp",
  slack: "Slack",
  teams: "Teams",
};

export const DATA_REGIONS = ["uk", "eu"] as const;
export type DataRegion = (typeof DATA_REGIONS)[number];

export const AI_FUNDING = ["credits", "byok"] as const;
export type AiFunding = (typeof AI_FUNDING)[number];

/**
 * Setup's saved steps. "website" asks about the business (its name, industry, website and size),
 * "goals" the first goal, and "tools" the apps with what Chief may do folded in; "access" stays
 * for progress saved before it was folded. Chief works out the rest and proposes the team.
 */
export const WIZARD_STEPS = ["website", "goals", "tools", "access", "team", "plan"] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

const text = (max: number) => z.string().trim().max(max);
const unique = <T extends z.ZodType>(item: T, max: number) =>
  z
    .array(item)
    .max(max)
    .transform((values) => [...new Set(values)]);

export const CUSTOM_DEPARTMENTS_MAX = 5;

/**
 * What the owner lets Chief do while it sets the company up. The server enforces each one in the
 * research run's instructions and tools. Missing values take SETUP_PERMISSION_DEFAULTS.
 */
export const SETUP_PERMISSIONS = ["website", "email", "departments", "memory", "tools"] as const;
export type SetupPermission = (typeof SETUP_PERMISSIONS)[number];
export const SetupPermissionsSchema = z.object({
  /** Read the company website. */
  website: z.boolean().optional(),
  /** Read the last 30 days of email, at summary level, from a connected mail app. */
  email: z.boolean().optional(),
  /** Create a sidebar section per department. */
  departments: z.boolean().optional(),
  /** Give each department its own memory zone. */
  memory: z.boolean().optional(),
  /** Suggest connecting the company's tools. */
  tools: z.boolean().optional(),
});
export type SetupPermissions = z.infer<typeof SetupPermissionsSchema>;
export const SETUP_PERMISSION_DEFAULTS: Record<SetupPermission, boolean> = {
  website: true,
  email: false,
  departments: true,
  memory: true,
  tools: true,
};

/** The owner's choices with the defaults filled in. */
export function resolveSetupPermissions(
  permissions: SetupPermissions | undefined,
): Record<SetupPermission, boolean> {
  return { ...SETUP_PERMISSION_DEFAULTS, ...permissions };
}

/**
 * The company website as saved: an http(s) URL on a real host, a bare domain read as https.
 * Empty clears it. Null when the text is not a website, such as a stray letter.
 */
export function normalizeCompanyWebsite(value: string): string | null {
  const typed = value.trim();
  if (!typed) return "";
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(typed) ? typed : `https://${typed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (url.username || url.password || /\s/.test(typed)) return null;
  // A real host: a name with a dot and a top-level domain of letters, like example.co.uk, or an
  // IPv4 address.
  const domain = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i.test(url.hostname);
  const ipv4 = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(url.hostname);
  if (!domain && !ipv4) return null;
  // Kept as the owner typed it, with https added to a bare domain.
  return withScheme;
}

export const CompanyAnswersSchema = z.object({
  name: text(120).optional(),
  website: text(200).optional(),
  industry: z.enum(INDUSTRIES).optional(),
  /** Free text when the industry is "other". */
  industryOther: text(80).optional(),
  /** ISO 3166-1 alpha-2. */
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  size: z.enum(COMPANY_SIZES).optional(),
  description: text(500).optional(),
  departments: unique(z.enum(DEPARTMENTS), DEPARTMENTS.length).optional(),
  customDepartments: unique(text(60).min(1), CUSTOM_DEPARTMENTS_MAX).optional(),
  goals: unique(z.enum(GOALS), GOALS.length).optional(),
  goalsOther: text(300).optional(),
  /** What the owner wants to achieve, in their words. */
  goalsText: text(1000).optional(),
  /** When they want it by: an ISO date, or empty for none. */
  deadline: z.union([z.iso.date(), z.literal("")]).optional(),
  autonomy: z.enum(AUTONOMY_LEVELS).optional(),
  tools: unique(z.enum(TOOLS), TOOLS.length).optional(),
  toolsOther: text(300).optional(),
  /** Stored for the account; Nexamero does not run separate regions yet. */
  dataRegion: z.enum(DATA_REGIONS).optional(),
  aiFunding: z.enum(AI_FUNDING).optional(),
  permissions: SetupPermissionsSchema.optional(),
});
export type CompanyAnswers = z.infer<typeof CompanyAnswersSchema>;

/** One bot of the recommended team. `key` names where it came from: a department, Chief or custom. */
export const TeamBotSchema = z.object({
  key: z.string().min(1).max(80),
  name: z.string(),
  role: z.string(),
  tools: z.array(z.string()),
  /** The memory zone it starts in, by name; null for Chief, who reads the open zones. */
  zone: z.string().nullable(),
  /** Its zone is closed to Chief and its bot is private to the owner. */
  walled: z.boolean(),
  superBot: z.boolean(),
  /** Suggested by the goals but left off until switched on. */
  suggested: z.boolean(),
});
export type TeamBot = z.infer<typeof TeamBotSchema>;

export const TEAM_BOTS_MAX = 20;

/**
 * The team Chief proposes after researching the business: what it found, the industry playbook
 * it wrote, and the leads to create. Everything here is generated per company; nothing about an
 * industry is built into the product.
 */
const line = (max: number) => z.string().trim().min(1).max(max);

/** "Also suggested" leads one proposal may carry. */
export const TEAM_EXTRAS_MAX = 4;

/** Routines one lead may suggest. */
export const AUTO_TEAM_ROUTINES_MAX = 4;

/** A routine a lead suggests: a name, what each run does, and a five-field cron schedule. */
export const AutoTeamRoutineSchema = z.object({
  name: line(80),
  prompt: line(2000),
  cron: z
    .string()
    .trim()
    .regex(/^\S+( \S+){4}$/, "Use a five-field cron schedule, such as 0 8 * * 1-5"),
});
export type AutoTeamRoutine = z.infer<typeof AutoTeamRoutineSchema>;

export const AutoTeamLeadSchema = z.object({
  /** A person-like first name, e.g. "Maya". */
  firstName: line(40),
  /** e.g. "Operations Lead (Control Room & Cleaning)". */
  title: line(BOT_TITLE_MAX_LENGTH),
  /** The sidebar section (department) the lead sits in. */
  section: line(60),
  /** The memory zone the lead works in. */
  zone: line(60),
  description: line(200),
  /** How the lead works, written from the playbook and the company facts. */
  instructions: line(8000),
  /** The department role recipe the lead is built from, when one fits. */
  recipe: z.enum(ROLE_RECIPE_IDS).optional(),
  /**
   * The lead's routines, adapted from its recipe to this business. Approving creates them
   * paused; the owner switches each one on. Omitted: the recipe's own routines.
   */
  routines: z.array(AutoTeamRoutineSchema).max(AUTO_TEAM_ROUTINES_MAX).optional(),
});
export type AutoTeamLead = z.infer<typeof AutoTeamLeadSchema>;

export const ProposeTeamInputSchema = z.object({
  company: z.object({
    name: text(120).optional(),
    industry: line(120),
    country: z
      .string()
      .regex(/^[A-Z]{2}$/)
      .describe("Where the business is based, as an ISO 3166-1 alpha-2 code such as GB.")
      .optional(),
    /** Two or three sentences on what the business does, for whom and where. */
    summary: line(600),
    /** Researched facts, proposed to the owner for the Company zone once the team exists. */
    facts: z.array(ProposedFactSchema).max(FACTS_PER_PROPOSAL_MAX).default([]),
  }),
  /** The industry playbook, saved as a skill every bot in the Space can read. */
  playbook: z.object({
    name: line(80),
    description: line(300),
    /** Markdown: rules and standards with official sources, checklists, key dates, pricing, risks, glossary. */
    body: line(60_000),
  }),
  leads: z.array(AutoTeamLeadSchema).min(1).max(TEAM_BOTS_MAX),
  /**
   * Leads that would also help but the business can start without. They are left off the team
   * unless the owner ticks them before approving.
   */
  extras: z.array(AutoTeamLeadSchema).max(TEAM_EXTRAS_MAX).optional(),
  /**
   * Facts for one department, by its section name, such as what email showed about its
   * customers, suppliers, recurring work and deadlines. Each set is proposed to the owner for that
   * department's zone, or the Company zone when departments get no zone.
   */
  departmentFacts: z
    .array(
      z.object({
        section: line(60),
        facts: z.array(ProposedFactSchema).min(1).max(FACTS_PER_PROPOSAL_MAX),
      }),
    )
    .max(TEAM_BOTS_MAX)
    .optional(),
});
export type ProposeTeamInput = z.infer<typeof ProposeTeamInputSchema>;

/**
 * Where Chief is in setting the company up. `waiting_model`: the goals are in but no model can
 * run yet. `question` and `review`: Chief is waiting on its one question or the team approval.
 * `stopped`: its research run ended without a team; starting setup again retries it.
 */
export const SETUP_PHASES = [
  "idle",
  "waiting_model",
  "researching",
  "question",
  "review",
  "stopped",
  "done",
] as const;
export type SetupPhase = (typeof SETUP_PHASES)[number];

const SetupLeadSchema = z.object({ name: z.string(), title: z.string(), section: z.string() });

/** The leads the owner keeps before approving, by first name, from those Chief proposed. */
export const ChooseTeamInputSchema = z.object({
  leads: z
    .array(z.string().trim().min(1).max(40))
    .min(1)
    .max(TEAM_BOTS_MAX + TEAM_EXTRAS_MAX),
});
export type ChooseTeamInput = z.infer<typeof ChooseTeamInputSchema>;

export const SetupStatusSchema = z.object({
  phase: z.enum(SETUP_PHASES),
  chiefId: Id.nullable(),
  /** The card Chief is waiting on, answered like any other ask. */
  ask: z.object({ runId: Id, messageId: Id, block: AskMessageBlock }).nullable(),
  /**
   * While Chief researches: the steps its run has taken so far, oldest first, as the thread's
   * tool status lines ("Reading page: …"). Optional so older servers still parse.
   */
  steps: z.array(z.string()).optional(),
  /** The proposed team while it waits for approval. */
  team: z
    .object({
      leads: z.array(SetupLeadSchema),
      /** Leads Chief also suggested, off the team unless the owner ticks them. */
      extras: z.array(SetupLeadSchema).default([]),
      playbook: z.string(),
      /** Bots billed on top of the plan once the team exists. */
      extraBots: z.number().int().nonnegative(),
      /** Bots the plan includes before extras are billed; null when none are billed. */
      includedBots: z.number().int().nonnegative().nullable().default(null),
    })
    .nullable(),
});
export type SetupStatus = z.infer<typeof SetupStatusSchema>;

export const CompanyStateSchema = z.object({
  answers: CompanyAnswersSchema,
  /** The last step saved, so a reload resumes after it. */
  step: z.enum(WIZARD_STEPS).nullable(),
  /** The wizard is still owed for this Space: its setup is neither finished nor skipped. */
  wizard: z.boolean(),
  teamCreated: z.boolean(),
  completed: z.boolean(),
  /** Setup ended without a team, so Settings offers to open it again. */
  reopen: z.boolean().default(false),
  /** The deployment sells plans, so the wizard ends on the plan step. */
  plans: z.boolean(),
  /** Bots the plan lets the team have on top of Chief; null is no cap. */
  botLimit: z.number().int().nonnegative().nullable(),
  /** Which AI funding choices this deployment and plan offer; none hides the question. */
  funding: z.array(z.enum(AI_FUNDING)),
  /** The web page that runs the wizard, for apps that send the owner there. */
  setupUrl: z.string().url(),
  setup: SetupStatusSchema,
});
export type CompanyState = z.infer<typeof CompanyStateSchema>;

export const FIRST_DAY_ITEMS = ["team", "app", "invite", "decision"] as const;
export type FirstDayItem = (typeof FIRST_DAY_ITEMS)[number];

export const FirstDaySchema = z.object({
  /** Shown to an admin whose Space was set up by the wizard, until dismissed or all done. */
  visible: z.boolean(),
  /** The wizard is unfinished: the app sends the admin back to it. */
  wizard: z.boolean(),
  done: z.record(z.enum(FIRST_DAY_ITEMS), z.boolean()),
  /** Chief's bot, which the card sits above. */
  chiefId: Id.nullable(),
  /** The tools the Connect step offers first: the company's own, else the usual business apps. */
  apps: z.array(z.enum(TOOLS)).default([]),
  /** The connectable apps the owner ticked at setup, each its own Connect item. */
  connect: z.array(z.object({ tool: z.enum(TOOLS), done: z.boolean() })).default([]),
});
export type FirstDay = z.infer<typeof FirstDaySchema>;
